import Constant from '@/const'
import Store from '@/store/index.js'
import moment from 'moment'

// ==================== 定数 ====================

// 結果コード
const RESULT_CODE = {
  SUCCESS: 0, // 料金計算成功
  NO_PRICE: 1, // 料金なし（別途見積）
}

// オプション区分
const OPTION_KBN = {
  ETC: '01', // ETCセットアップ
  SYAKO: '02', // 車庫証明
  SYAKO_KEI: '03', // 車庫証明（軽自動車）
  KIBOU_NUM: '04', // 希望ナンバー
  SATURDAY: '05', // 土曜日輸送
}

// 別途見積が必要な燃料タイプ（電気自動車、燃料電池、水素車）
const SPECIAL_FUEL_TYPES = [4, 5, 8]

// 車両区分
const VEHICLE_TYPE = {
  JISOU_SELF_DRIVING: 1, // 自走可能
  SYARYO_PASSENGER: 1, // 普通車
}

const STANDARD_VEHICLE_HEIGHT_LIMIT = 170

// ==================== 汎用ヘルパー関数 ====================

/**
 * 2つの値が等しいか判定（null, 空文字, undefined は同じとみなす）
 * @param {any} a - 比較する値A
 * @param {any} b - 比較する値B
 * @returns {boolean} - 等しい場合true
 */
const isEqualValue = (a, b) => {
  const isNullish = (val) => val === null || val === '' || val === undefined
  return isNullish(a) ? isNullish(b) : Number(a) === Number(b)
}

/**
 * オークション会場かどうか判定
 * @param {Object} form - フォームデータ
 * @param {string} hikino - 'HIKITORI' or 'NOSYA'
 * @returns {boolean} - オークション会場の場合true
 */
const isAuctionPlace = (form, hikino) => {
  return Number(form[`${hikino}_AA_FLG`]) === 1
}

// ==================== 引納先データ抽出関数 ====================

/**
 * 引納先の場所タイプを取得（API用）
 * @param {Object} form - フォームデータ
 * @param {string} hikino - 'HIKITORI' or 'NOSYA'
 * @returns {number} - 場所タイプ
 */
const extractPlaceType = (form, hikino) => {
  const placeType = form[`${hikino}_PLACE_TYPE`]

  if (placeType === 'torokubasyo') {
    return Constant.RYOKIN_API_AREA_TYPE.TOROKU
  }

  // auction, direct, okiniiri の場合
  return isAuctionPlace(form, hikino)
    ? Constant.RYOKIN_API_AREA_TYPE.AUCTION
    : Constant.RYOKIN_API_AREA_TYPE.AREA
}

/**
 * 引納先が個人かどうか取得
 * @param {Object} form - フォームデータ
 * @param {string} hikino - 'HIKITORI' or 'NOSYA'
 * @returns {boolean} - 個人の場合true
 */
const extractIsPersonal = (form, hikino) => {
  const placeType = form[`${hikino}_PLACE_TYPE`]

  // 登録場所・オークションは法人
  if (placeType === 'torokubasyo' || placeType === 'auction') {
    return false
  }

  // direct, okiniiri の場合
  if (isAuctionPlace(form, hikino)) {
    return false
  }

  return form[`${hikino}_YUSO_KBN`] === Constant.YUSOKBN.KOJIN
}

/**
 * 引納先の市区コードを取得
 * @param {Object} form - フォームデータ
 * @param {string} hikino - 'HIKITORI' or 'NOSYA'
 * @returns {string|null} - 市区コード
 */
const extractAreaCode = (form, hikino) => {
  const placeType = form[`${hikino}_PLACE_TYPE`]

  if (placeType === 'torokubasyo') {
    return null
  }

  // auction, direct, okiniiri の場合
  return isAuctionPlace(form, hikino)
    ? form[`${hikino}_AA_CD`]
    : form[`${hikino}_SHIKU_CD`]
}

/**
 * 引納先の登録場所IDを取得
 * @param {Object} form - フォームデータ
 * @param {string} hikino - 'HIKITORI' or 'NOSYA'
 * @returns {string|null} - 登録場所ID
 */
const extractRegisteredPlaceId = (form, hikino) => {
  return form[`${hikino}_PLACE_TYPE`] === 'torokubasyo'
    ? form[`${hikino}_PLACE_REF_ID`]
    : null
}

// ==================== APIリクエスト生成 ====================

/**
 * 料金計算APIのリクエストデータを生成
 * @param {Object} syaryo - 車両データ
 * @param {Object} form - フォームデータ
 * @returns {Object} APIリクエストデータ
 */
const createPriceCalculationRequest = (syaryo, form) => {
  return {
    JYUCYU_SHIKI_CD: Store.state.user.data.JYUCYU_SHIKI_CD,
    MEIHEN_FLG: form.MEIHEN_FLG,
    syaryo: syaryo,
    hikitori: {
      PLACE_TYPE: extractPlaceType(form, 'HIKITORI'),
      IS_PERSONAL: extractIsPersonal(form, 'HIKITORI'),
      TOROKUBAYO_KO_ID: extractRegisteredPlaceId(form, 'HIKITORI'),
      SHIKI_CD: extractAreaCode(form, 'HIKITORI'),
    },
    nosya: {
      PLACE_TYPE: extractPlaceType(form, 'NOSYA'),
      IS_PERSONAL: extractIsPersonal(form, 'NOSYA'),
      TOROKUBAYO_KO_ID: extractRegisteredPlaceId(form, 'NOSYA'),
      SHIKI_CD: extractAreaCode(form, 'NOSYA'),
      NOSYA_KIBOU_BI: form.NOSYA_KIBOU_BI,
    },
  }
}

// ==================== 車両条件判定 ====================

/**
 * 自走可能な車両かどうか判定
 * @param {Object} syaryo - 車両データ
 * @returns {boolean}
 */
const isSelfDrivable = (syaryo) => {
  return (
    syaryo.JISOU_KBN === undefined ||
    Number(syaryo.JISOU_KBN) === VEHICLE_TYPE.JISOU_SELF_DRIVING
  )
}

/**
 * 乗用車タイプかどうか判定
 * @param {Object} syaryo - 車両データ
 * @returns {boolean}
 */
const isPassengerCarType = (syaryo) => {
  return Number(syaryo.SYARYO_TYPE_KBN) === VEHICLE_TYPE.SYARYO_PASSENGER
}

/**
 * 標準的な車両状態かどうか判定（カスタム・高級車・旧車でない）
 * @param {Object} carConditions - 車両状態
 * @returns {boolean}
 */
const isStandardVehicleCondition = (carConditions) => {
  return (
    // 自走可能
    (carConditions.is_self_run === undefined ||
      carConditions.is_self_run === 1) &&
    // エアコン作動
    (carConditions.is_run_air_conditioner === undefined ||
      carConditions.is_run_air_conditioner === 1) &&
    // カスタムなし
    (carConditions.is_custom === undefined || carConditions.is_custom === 0) &&
    // 高級車でない
    (carConditions.is_expensive === undefined ||
      carConditions.is_expensive === 0) &&
    // 旧車でない
    (carConditions.is_classic === undefined || carConditions.is_classic === 0)
  )
}

/**
 * 特殊燃料タイプでないか判定（電気自動車、燃料電池、水素車でない）
 * @param {Object} carConditions - 車両状態
 * @returns {boolean}
 */
const isNotSpecialFuelType = (carConditions) => {
  return (
    carConditions.fuel_type === undefined ||
    !SPECIAL_FUEL_TYPES.includes(carConditions.fuel_type)
  )
}

/**
 * 車両高さが標準範囲内か判定
 * @param {Object} syaryo - 車両データ
 * @returns {boolean} - 標準範囲内（170cm未満）の場合true
 */
const isValidVehicleHeight = (syaryo) => {
  return (
    !syaryo.SYARYO_TAKASA ||
    syaryo.SYARYO_TAKASA < STANDARD_VEHICLE_HEIGHT_LIMIT
  )
}

/**
 * 自動料金計算が可能な車両かどうか判定
 * @param {Object} syaryo - 車両データ
 * @returns {boolean} - 自動計算可能な場合true、別途見積が必要な場合false
 */
const canCalculatePriceAutomatically = (syaryo) => {
  const carConditions = syaryo.CAR_CONDITIONS

  return (
    isSelfDrivable(syaryo) &&
    isPassengerCarType(syaryo) &&
    !Number(syaryo.JYOTAI_FLG) && // 状態良好
    !Number(syaryo.HOKAN_FLG) && // 保管なし
    isStandardVehicleCondition(carConditions) &&
    isNotSpecialFuelType(carConditions) &&
    isValidVehicleHeight(syaryo)
  )
}

// ==================== 見積データ比較 ====================

/**
 * 見積時と発注時の引納先が一致するか判定
 * @param {Object} estimateData - 見積データ
 * @param {Object} form - フォームデータ
 * @param {string} hikino - 'HIKITORI' or 'NOSYA'
 * @returns {boolean} - 一致する場合true
 */
const isPlaceMatchingEstimate = (estimateData, form, hikino) => {
  const placeType = form[`${hikino}_PLACE_TYPE`]

  // 登録済みの場所
  if (placeType === 'torokubasyo') {
    return form[`${hikino}_PLACE_REF_ID`] == estimateData[`${hikino}_TOROKU_ID`]
  }

  // auction, direct, okiniiri
  if (
    placeType === 'auction' ||
    placeType === 'direct' ||
    placeType === 'okiniiri'
  ) {
    // オークション会場
    if (form[`${hikino}_AA_FLG`]) {
      return form[`${hikino}_AA_CD`] == estimateData[`${hikino}_AA_CD`]
    }

    // 直接入力（都道府県 + 市区で判定）
    return (
      form[`${hikino}_TODO_CD`] == estimateData[`${hikino}_TODO_CD`] &&
      form[`${hikino}_SHIKU_CD`] == estimateData[`${hikino}_SHIKU_CD`]
    )
  }

  return false
}

/**
 * 車両高さが見積時と発注時で同じ区分か判定
 * 170cm以上/未満で区分を分け、厳密な高さでは比較しない
 * @param {number|null} estimateHeight - 見積時の高さ
 * @param {number|null} orderHeight - 発注時の高さ
 * @returns {boolean} - 同じ区分の場合true
 */
const isSameHeightCategory = (estimateHeight, orderHeight) => {
  const limit = STANDARD_VEHICLE_HEIGHT_LIMIT

  // 見積時に高さ未設定の場合
  if (!estimateHeight) {
    return !orderHeight || orderHeight < limit
  }

  // 発注時に高さ未設定の場合
  if (!orderHeight) {
    return false
  }

  // 見積時は170cm以上、発注時は170cm未満
  if (estimateHeight >= limit && orderHeight < limit) {
    return false
  }

  // 見積時は170cm未満、発注時は170cm以上
  if (estimateHeight < limit && orderHeight >= limit) {
    return false
  }

  return true
}

/**
 * 見積時と発注時の車両情報が一致するか判定
 * @param {Object} estimateData - 見積データ
 * @param {Object} orderVehicle - 発注時の車両データ
 * @returns {boolean} - 一致する場合true
 */
const isVehicleMatchingEstimate = (estimateData, orderVehicle) => {
  // 料金に影響する項目のリスト
  const priceRelatedFields = [
    'ETC_SETUP_FLG',
    'MEIHEN_KIBO_NUMBER_FLG',
    'MEIHEN_KIBO_NUMBER_DAIKO_FLG',
    'SYAKOSYO_JYURYO_FLG',
    'HOKAN_FLG',
    'MEIHEN_KEI_FLG',
  ]

  // ハブの見積りでは常に表示されている項目だが、発注時の項目では条件付きで表示されるため、ここで追加する
  if (orderVehicle.JISOU_KBN !== undefined) {
    priceRelatedFields.push('JISOU_KBN')
  }

  // ハブの見積りで車両タイプが未入力かつ、発注時の車両タイプが普通車でない場合は差分を判定する
  if (
    !(
      estimateData.SYARYO_TYPE_KBN !== undefined &&
      orderVehicle.SYARYO_TYPE_KBN === VEHICLE_TYPE.SYARYO_PASSENGER
    )
  ) {
    priceRelatedFields.push('SYARYO_TYPE_KBN')
  }

  // いずれかの項目が異なる場合はfalse
  for (const field of priceRelatedFields) {
    if (!isEqualValue(orderVehicle[field], estimateData[field])) {
      return false
    }
  }

  // 車両高さの区分が一致するか確認
  return isSameHeightCategory(estimateData.TAKASA, orderVehicle.SYARYO_TAKASA)
}

// ==================== 見積料金処理 ====================

/**
 * 料金なしの結果オブジェクトを生成（別途見積が必要な場合）
 * @returns {{
 *   result_code: number,
 *   result_price: number,
 *   result_noki: number,
 *   isMitumori: boolean
 * }} 料金なしの結果オブジェクト
 */
const createNoPriceResult = () => ({
  result_code: RESULT_CODE.NO_PRICE,
  result_price: 0,
  result_noki: 0,
  isMitumori: false,
})

/**
 * 見積の有効期限をチェック
 * @param {Object} estimateData - 見積データ
 * @returns {boolean} - 有効期限内の場合true
 */
const isEstimateValid = (estimateData) => {
  const today = moment()
  const limit = moment(estimateData.LIMITED_NICHIJI.slice(0, 10))
  return today.isSameOrBefore(limit)
}

/**
 * 見積内訳データを生成
 * @param {Array} uchiwakeList - 内訳リスト
 * @returns {{items: Array<{name: string, price: number}>, total: number}} 整形済み内訳データ
 */
const createPriceBreakdown = (uchiwakeList) => {
  let total = 0
  const items = uchiwakeList.map((item) => {
    total += item.UCHIWAKE_PRICE
    return {
      name: item.UCHIWAKE_NAME,
      price: item.UCHIWAKE_PRICE,
    }
  })
  return { items, total }
}

/**
 * 土曜納車オプション料金を取得
 * @param {Array} optionList - オプションリスト
 * @returns {Object|null} - オプション情報またはnull
 */
const findSaturdayOption = (optionList) => {
  return optionList.find((option) => option.OPTION_KBN === OPTION_KBN.SATURDAY)
}

/**
 * 見積料金から結果オブジェクトを生成
 * @param {Object} estimateData - 見積データ
 * @param {Array} optionList - オプションリスト
 * @param {boolean} isMeihen - 名変フラグ
 * @param {boolean} isNosyaSaturday - 土曜納車フラグ
 * @returns {{
 *   result_code: number,
 *   result_price: number,
 *   result_noki: string,
 *   result_uchiwake: Array<{name: string, price: number}>,
 *   isMitumori: boolean,
 *   isAdjustment: boolean,
 *   saturdayOptionPrice: number
 * }} 見積結果オブジェクト
 */
const createResultFromEstimate = (
  estimateData,
  optionList,
  isMeihen,
  isNosyaSaturday
) => {
  const { items: uchiwake, total: uchiwakeTotal } = createPriceBreakdown(
    estimateData.uchiwake
  )

  // 見積料金と内訳料金の総和が違う場合は、見積料金が調整されている
  const isAdjustment = estimateData.RYOKIN !== uchiwakeTotal

  // 土曜納車料金を追加
  let totalPrice = estimateData.RYOKIN
  let saturdayOptionPrice = 0

  if (isMeihen && isNosyaSaturday) {
    const saturdayOption = findSaturdayOption(optionList)
    if (saturdayOption) {
      saturdayOptionPrice = Number(saturdayOption.OPTION_PRICE)
      totalPrice += saturdayOptionPrice
      uchiwake.push({
        name: saturdayOption.OPTION_MEI,
        price: saturdayOption.OPTION_PRICE,
      })
    }
  }

  return {
    result_code: RESULT_CODE.SUCCESS,
    result_price: totalPrice,
    result_noki: estimateData.NOUKI,
    result_uchiwake: uchiwake,
    isMitumori: true,
    isAdjustment,
    saturdayOptionPrice,
  }
}

/**
 * 料金計算APIを実行
 * @param {Object} requestData - リクエストデータ
 * @returns {Promise<Object>} API結果、エラー時は料金なし結果
 */
const executePriceCalculationApi = async (requestData) => {
  try {
    return await Store.dispatch('mitumori/ryokin', requestData)
  } catch (error) {
    // APIエラー時は別途見積として扱う
    console.error('料金計算API実行エラー:', error)
    return createNoPriceResult()
  }
}

/**
 * 新規で料金を計算
 * @param {Object} syaryo - 車両データ
 * @param {Object} form - フォームデータ
 * @returns {Promise<Object>} 計算結果
 */
const calculateNewPrice = async (syaryo, form) => {
  if (!canCalculatePriceAutomatically(syaryo)) {
    return createNoPriceResult()
  }

  const requestData = createPriceCalculationRequest(syaryo, form)
  const result = await executePriceCalculationApi(requestData)

  // 納期に「日」を付与
  if (Number(result.result_noki)) {
    result.result_noki += '日'
  }

  return result
}

// ==================== エクスポート関数 ====================

/**
 * 見積結果を取得する（メイン関数）
 *
 * 見積から発注した車両の場合は見積データを使用し、
 * 新規の場合は自動料金計算APIを実行します。
 *
 * @param {Object} params - パラメータ
 * @param {Object} params.syaryo - 車両データ
 * @param {Object} params.form - フォームデータ
 * @param {Object} params.mitumori - 見積データ
 * @param {Array} params.optionList - オプションリスト
 * @param {boolean} params.isMeihen - 名変フラグ
 * @param {boolean} params.isNosyaSaturday - 土曜納車フラグ
 * @returns {Promise<{
 *   result_code: number,        // 0: 成功, 1: 料金なし（別途見積）
 *   result_price: number,       // 料金（円）
 *   result_noki: string|number, // 納期
 *   result_uchiwake?: Array<{name: string, price: number}>, // 内訳（見積からの場合のみ）
 *   isMitumori: boolean,        // 見積データを使用したか
 *   isAdjustment?: boolean,     // 料金調整されているか（見積からの場合のみ）
 *   saturdayOptionPrice?: number // 土曜納車オプション料金（見積からの場合のみ）
 * }>} 見積結果オブジェクト
 */
export const calcMitumoriResult = async ({
  syaryo,
  form,
  mitumori,
  optionList,
  isMeihen,
  isNosyaSaturday,
}) => {
  // 見積から発注した車両の場合
  if (syaryo.isMitumori) {
    // 見積が有効かチェック
    const isValidEstimate =
      isEstimateValid(mitumori) &&
      isPlaceMatchingEstimate(mitumori, form, 'HIKITORI') &&
      isPlaceMatchingEstimate(mitumori, form, 'NOSYA') &&
      isVehicleMatchingEstimate(mitumori, syaryo) &&
      isStandardVehicleCondition(syaryo.CAR_CONDITIONS) &&
      isNotSpecialFuelType(syaryo.CAR_CONDITIONS)
    if (!isValidEstimate) {
      return createNoPriceResult()
    }

    return createResultFromEstimate(
      mitumori,
      optionList,
      isMeihen,
      isNosyaSaturday
    )
  }

  // 新規で料金を計算
  return calculateNewPrice(syaryo, form)
}



// WEBPACK FOOTER //
// ./src/components/order/utils/confirmCalcUtils.js